import {returnIfBoundingBox3D} from '@wandb/weave/common/types/media';
import {isEmpty} from 'lodash';
import {useMemo} from 'react';

import {type ClassLabelMap} from '../../../util/media';
import {
  WANDB_3DBBOX_CLASS_LABEL_KEY,
  WANDB_DELIMETER,
} from '../../../util/mediaConstants';
import {BOXES, GROUP_NAME_3D_BOUNDING_BOXES} from '../common';
import {eachBoundingBox} from '../utils/eachBoundingBox';
import {type BoundingBoxData, useBoxData} from './useBoxData';

// This method is a workaround. For 2d bounding boxes, the SDK puts bounding box
// labels/scores in run metadata where it's cheap to read, but for 3d bounding
// boxes, the SDK doesn't do that, so instead we use data from the point clouds
// we've loaded for display in the UI.
const getBoundingBoxesFor3d = (
  allMediaKeys: string[] | undefined,
  boxData: BoundingBoxData | undefined
): ClassLabelMap => {
  if (!allMediaKeys || !boxData) {
    return {};
  }

  const boxLabels: Record<number, string> = {};
  const all3dBoxes: ClassLabelMap = {};
  // 2d bounding boxes support categories of box labels (box_captions),
  // but 3d only supports 1 category, so that's hardcoded here.
  //
  // GROUP_NAME_3D_BOUNDING_BOXES is displayed to the user as the group label.
  // key just needs to be unique, so hard coded 3d_bounding_boxes for that.
  all3dBoxes[BOXES] = {};
  all3dBoxes[BOXES][GROUP_NAME_3D_BOUNDING_BOXES] = {
    key: 'boxes' + WANDB_DELIMETER + '3d_bounding_boxes',
    type: WANDB_3DBBOX_CLASS_LABEL_KEY,
    value: boxLabels,
  };

  for (const boundingBox of eachBoundingBox(allMediaKeys, boxData)) {
    const bbox3d = returnIfBoundingBox3D(boundingBox);
    if (bbox3d) {
      // we want to limit the number of labels since the number of labels
      // in a file is unbounded, but the UI can only display so many
      if (bbox3d.classInfo) {
        // boxLabels is a map instead of an array for compatibility with
        // 2d bounding box classes, which have class ids where the user
        // sets the numeric value
        boxLabels[bbox3d.classInfo.id] = bbox3d.classInfo.label;
      }
    }
  }

  return all3dBoxes;
};

const has3dBoundingBoxes = (
  allMediaKeys: string[] | undefined,
  boxData: BoundingBoxData | undefined
) => {
  for (const boundingBox of eachBoundingBox(allMediaKeys, boxData)) {
    if (returnIfBoundingBox3D(boundingBox)) {
      return true;
    }
  }
  return false;
};

/**
 * Return a uniformly-formatted data object for bounding boxes shown in a media
 * panel. This can accept a collection of 2d bounding boxes, or can get 3d
 * bounding boxes out of the application's redux state. Results are memoized and
 * should be performant even for large numbers of bounding boxes.
 *
 * @param allBoxes - Bounding boxes that come in from props, usually 2d BBs.
 * @param allMediaKeys - Media keys for fishing out bounding box information
 *    from the global state, which is where 3d bounding box info is saved.
 * @param panelRefId
 *    the panel id that holds bounding box information in the global state atom.
 */
export function useBoundingBoxes(
  allBoxes: ClassLabelMap,
  allMediaKeys: string[] | undefined,
  panelRefId: string
) {
  const boxData = useBoxData(panelRefId);
  return useMemo(() => {
    if (!isEmpty(allBoxes)) {
      return allBoxes;
    }
    if (has3dBoundingBoxes(allMediaKeys, boxData)) {
      return getBoundingBoxesFor3d(allMediaKeys, boxData);
    }
    return {};
  }, [allBoxes, allMediaKeys, boxData]);
}
